/**
 * Where the spotlight and the tooltip card go — pure rectangle arithmetic
 * over numbers the provider read from `getBoundingClientRect`, so the
 * flip-and-clamp rules are provable without a DOM.
 */
import type { TourPlacement } from "./steps";

export interface Box {
  top: number;
  left: number;
  width: number;
  height: number;
}

export interface Size {
  width: number;
  height: number;
}

export type Side = "top" | "bottom" | "left" | "right";

/** Breathing room between the anchor's edge and the spotlight's. */
export const SPOT_PAD = 8;
/** Gap between the spotlight and the card. */
export const CARD_GAP = 12;
/** The card never comes closer than this to a viewport edge. */
export const VIEW_MARGIN = 12;

const AUTO_ORDER: readonly Side[] = ["bottom", "top", "right", "left"];

export function spotlightBox(anchor: Box, pad: number = SPOT_PAD): Box {
  return { top: anchor.top - pad, left: anchor.left - pad, width: anchor.width + pad * 2, height: anchor.height + pad * 2 };
}

/** The sides to try, preferred first. `auto` prefers below the anchor. */
export function sideOrder(placement: TourPlacement): Side[] {
  if (placement === "auto") return [...AUTO_ORDER];
  return [placement, ...AUTO_ORDER.filter((s) => s !== placement)];
}

function fits(side: Side, spot: Box, card: Size, view: Size): boolean {
  switch (side) {
    case "bottom": return spot.top + spot.height + CARD_GAP + card.height <= view.height - VIEW_MARGIN;
    case "top": return spot.top - CARD_GAP - card.height >= VIEW_MARGIN;
    case "right": return spot.left + spot.width + CARD_GAP + card.width <= view.width - VIEW_MARGIN;
    case "left": return spot.left - CARD_GAP - card.width >= VIEW_MARGIN;
  }
}

function clamp(v: number, lo: number, hi: number): number {
  // A card wider than the viewport pins to the margin rather than off-screen.
  return hi < lo ? lo : Math.min(hi, Math.max(lo, v));
}

/**
 * Card position for a spotlight: the first side (in preference order) with
 * room wins; when none has room the preferred side is used and the card is
 * clamped inside the viewport, which is always readable even if it then
 * overlaps the spotlight.
 */
export function placeCard(spot: Box, card: Size, view: Size, placement: TourPlacement): { top: number; left: number; side: Side } {
  const order = sideOrder(placement);
  const side = order.find((s) => fits(s, spot, card, view)) ?? order[0];
  const cx = spot.left + spot.width / 2;
  const cy = spot.top + spot.height / 2;
  let top: number;
  let left: number;
  switch (side) {
    case "bottom": top = spot.top + spot.height + CARD_GAP; left = cx - card.width / 2; break;
    case "top": top = spot.top - CARD_GAP - card.height; left = cx - card.width / 2; break;
    case "right": left = spot.left + spot.width + CARD_GAP; top = cy - card.height / 2; break;
    case "left": left = spot.left - CARD_GAP - card.width; top = cy - card.height / 2; break;
  }
  return {
    side,
    top: clamp(top, VIEW_MARGIN, view.height - card.height - VIEW_MARGIN),
    left: clamp(left, VIEW_MARGIN, view.width - card.width - VIEW_MARGIN),
  };
}

/**
 * The CSS custom property the phone card publishes while it is up: its own
 * height plus CARD_GAP, in px. The overlay is the ONLY writer; the app shell's
 * bottom padding and the Studio's chat drawer read it, so whatever sits at
 * the bottom of the page (the last control, the chat's composer) can move
 * above the sheet instead of under it. Unset (desktop, no tour) → 0px.
 */
export const TOUR_SHEET_VAR = "--ql-tour-sheet";
/** The read side, for inline `calc()` styles. */
export const TOUR_SHEET_PAD = `var(${TOUR_SHEET_VAR}, 0px)`;

/**
 * Phones: the card is a bottom sheet, so the viewport the spotlight can use
 * is only what is left ABOVE it. Returns the document scroll delta (px,
 * positive = down) that centres the anchor's spotlight in that free zone —
 * 0 when it already sits fully inside it (a control that is in view is not
 * moved), and a spotlight taller than the zone pins to the top margin.
 * `scrollIntoView({block:"center"})` alone centres on the WHOLE viewport and
 * parks a low control under the sheet (QA 2026-09-17: "Start the build").
 */
export function sheetScrollDelta(anchor: Box, sheetHeight: number, view: Size): number {
  const free = view.height - Math.max(0, sheetHeight) - CARD_GAP;
  const top = anchor.top - SPOT_PAD;
  const bottom = anchor.top + anchor.height + SPOT_PAD;
  if (top >= VIEW_MARGIN && bottom <= free) return 0;
  const boxH = bottom - top;
  const targetTop = boxH >= free - 2 * VIEW_MARGIN ? VIEW_MARGIN : Math.round((free - boxH) / 2);
  return Math.round(top - targetTop);
}
